import React, { useEffect, useState } from 'react';
import { Box, Typography, ToggleButtonGroup, ToggleButton, Stack } from '@mui/material';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import WidgetCard from './WidgetCard';
import MainCardTitle from './MainCardTitle';
import CenteredSpinner from '../../../components/CenteredSpinner';
import DashboardServices from '../../../services/DashboardServices';
import localStorageService from '../../../services/localStorageService';

const CaseTrendsOverTime = ({ title = "Case Trend - Over time" }) => {
    const [loading, setLoading] = useState(true);
    const [chartData, setChartData] = useState([]);
    const [period, setPeriod] = useState('weekly');
    const [stats, setStats] = useState({ increase: 0, year: new Date().getFullYear() });

    // Format date based on period type
    const formatDate = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            if (isNaN(date.getTime())) return dateString;

            if (period === 'daily') {
                // Show date (e.g., "Jan 15")
                const month = date.toLocaleString('en-US', { month: 'short' });
                const day = date.getDate();
                return `${month} ${day}`;
            } else if (period === 'weekly') {
                // Show week (e.g., "Week 1", "Week 2")
                const weekNumber = Math.ceil(date.getDate() / 7);
                const month = date.toLocaleString('en-US', { month: 'short' });
                return `${month} W${weekNumber}`;
            } else if (period === 'monthly') {
                // Show month (e.g., "Jan", "Feb")
                return date.toLocaleString('en-US', { month: 'short' });
            }

            return dateString;
        } catch (err) {
            return dateString;
        }
    };

    useEffect(() => {
        let mounted = true;
        const load = async () => {
            setLoading(true);
            try {
                // Get params with organization_id if applicable
                const storedUser = localStorageService?.getItem?.('user') ?? null;
                const roles = (storedUser?.roles) || (storedUser?.role) || storedUser?.rolesArray || [];
                const normalizedRoles = Array.isArray(roles) ? roles.map(r => String(r).toLowerCase()) : [];
                const targetRoles = ['organization admin', 'case officer', 'case manager', 'organization head','director cid','director ncpa', 'deputy director','oic','department head','team leader first respondent','call center officer mowca', 'counselling officer', 'legal officer'];
                const hasTargetRole = normalizedRoles.some(r => targetRoles.includes(r));

                const params = {
                    period: period
                };

                if (hasTargetRole) {
                    const orgId = storedUser?.employee?.organization_id ?? storedUser?.organization_id ?? storedUser?.org?.id ?? null;
                    if (orgId) params.organization_id = orgId;
                }

                const targetEmployee_id_Roles = ['case officer', 'first respondent', 'counselling officer', 'legal officer'];
                const needToPassUserId = normalizedRoles.some(r => targetEmployee_id_Roles.includes(r));
                if (needToPassUserId) {
                    const employeeId = storedUser?.employee?.id ?? storedUser?.employee_id ?? null;
                    if (employeeId) params.assigned_to_officer_id = employeeId;
                }
                const res = await DashboardServices.getDashboardCaseTrendsOverTime(params);
                const payload = res?.data ?? res;
                const dataArray = payload?.view?.data ?? payload?.data ?? payload ?? [];

                // Transform data for chart
                const formatted = Array.isArray(dataArray)
                    ? dataArray.map((item) => ({
                        date: item.date ?? item.period ?? item.label ?? '',
                        cases: Number(item.case_count) || 0,
                        label: item.label ?? item.date ?? ''
                    }))
                    : [];

                // Calculate stats (increase percentage and year)
                if (formatted.length > 1) {
                    const firstValue = formatted[0].cases;
                    const lastValue = formatted[formatted.length - 1].cases;
                    const increase = firstValue > 0 ? Math.round(((lastValue - firstValue) / firstValue) * 100) : 0;
                    setStats({ increase, year: new Date().getFullYear() });
                }

                console.log('Case Trends Over Time Data:', formatted);
                if (mounted) setChartData(formatted);
            } catch (err) {
                console.error('Failed to load case trends over time data', err);
            } finally {
                if (mounted) setLoading(false);
            }
        };

        load();
        return () => { mounted = false; };
    }, [period]);

    const handlePeriodChange = (event, newPeriod) => {
        if (newPeriod !== null) {
            setPeriod(newPeriod);
        }
    };

    // if (loading) return <WidgetCard><CenteredSpinner /></WidgetCard>;

    return (
        <WidgetCard>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 2 }}>
                <Box>
                    <Typography variant="h6" sx={{ fontWeight: 600, fontSize: 16, mb: 0.5 }}>
                        {title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        (+{stats.increase > 0 ? stats.increase : 0}) more in {stats.year}
                    </Typography>
                </Box>
                <ToggleButtonGroup
                    value={period}
                    exclusive
                    onChange={handlePeriodChange}
                    size="small"
                    sx={{
                        '& .MuiToggleButton-root': {
                            textTransform: 'capitalize',
                            px: 2,
                            py: 0.5,
                            fontSize: 12,
                            border: '1px solid #e0e0e0',
                            '&.Mui-selected': {
                                bgcolor: 'primary.main',
                                color: 'white',
                                '&:hover': {
                                    bgcolor: 'primary.dark',
                                }
                            }
                        }
                    }}
                >
                    <ToggleButton value="daily">Daily</ToggleButton>
                    <ToggleButton value="weekly">Weekly</ToggleButton>
                    <ToggleButton value="monthly">Monthly</ToggleButton>
                </ToggleButtonGroup>
            </Stack>

            {chartData.length === 0 ? (
                <Typography variant="body2" color="text.secondary" align="center" sx={{ py: 4 }}>
                    No data available
                </Typography>
            ) : (
                <Box sx={{ width: '100%', height: 300, mt: 2 }}>
                    <ResponsiveContainer width="100%" height="100%">
                        <AreaChart
                            data={chartData}
                            margin={{ top: 10, right: 30, left: 0, bottom: 0 }}
                        >
                            <defs>
                                <linearGradient id="colorCases" x1="0" y1="0" x2="0" y2="1">
                                    <stop offset="5%" stopColor="#7cb342" stopOpacity={0.3} />
                                    <stop offset="95%" stopColor="#7cb342" stopOpacity={0} />
                                </linearGradient>
                            </defs>
                            <CartesianGrid strokeDasharray="3 3" stroke="#e0e0e0" />
                            <XAxis
                                dataKey="date"
                                tick={{ fill: '#999', fontSize: 11 }}
                                axisLine={{ stroke: '#e0e0e0' }}
                                tickFormatter={(value) => formatDate(value)}
                            />
                            <YAxis
                                tick={{ fill: '#999', fontSize: 11 }}
                                axisLine={{ stroke: '#e0e0e0' }}
                            />
                            <Tooltip
                                contentStyle={{
                                    backgroundColor: '#fff',
                                    border: '1px solid #ccc',
                                    borderRadius: 4
                                }}
                            />
                            <Area
                                type="monotone"
                                dataKey="cases"
                                stroke="#7cb342"
                                strokeWidth={2}
                                fillOpacity={1}
                                fill="url(#colorCases)"
                            />
                        </AreaChart>
                    </ResponsiveContainer>
                </Box>
            )}
        </WidgetCard>
    );
};

export default CaseTrendsOverTime;
